import { useEffect, useMemo, useRef, useState } from "react";
import client from "../api/client";

const companyId = () => Number(localStorage.getItem("companyId") || 0);

const val = (r, ...keys) => {
  for (const k of keys) {
    const x = r?.[k];
    if (x !== undefined && x !== null) return x;
  }
  return "";
};

const safe = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const errText = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data;

  if (typeof d === "string" && d.trim()) return d.trim();
  if (d?.message && String(d.message).trim()) return String(d.message).trim();
  if (d?.title && String(d.title).trim()) return String(d.title).trim();

  if (d?.errors && typeof d.errors === "object") {
    const firstError = Object.values(d.errors).flat().find(Boolean);
    if (firstError) return String(firstError);
  }

  if (e?.message && String(e.message).trim()) return String(e.message).trim();

  return fallback;
};

const styles = {
  page: { fontSize: 11, padding: 0 },
  card: {
    background: "#fff",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
  },
  header: {
    background: "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
    color: "#fff",
    padding: "9px 12px",
  },
  title: { fontSize: 13, fontWeight: 800, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 32, fontSize: 11, borderRadius: 7 },
  btn: { height: 32, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  tableWrap: {
    maxHeight: 410,
    overflow: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 1050, marginBottom: 0 },
  th: {
    fontSize: 10,
    color: "#fff",
    background: "#07111f",
    padding: "7px 8px",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: { fontSize: 11, padding: "6px 8px", whiteSpace: "nowrap" },
};

export default function POSBillingPage() {
  const barcodeRef = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);

  const [barcode, setBarcode] = useState("");
  const [cart, setCart] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const [form, setForm] = useState({
    posNo: "",
    customerId: "",
    warehouseId: "",
    paymentMode: "CASH",
    paidAmount: "",
    notes: "",
  });

  const totals = useMemo(() => {
    const subTotal = cart.reduce((s, x) => s + num(x.qty) * num(x.rate), 0);

    const discountAmount = cart.reduce(
      (s, x) => s + (num(x.qty) * num(x.rate) * num(x.discountPercent)) / 100,
      0,
    );

    const taxAmount = cart.reduce((s, x) => {
      const line = num(x.qty) * num(x.rate);
      const discount = (line * num(x.discountPercent)) / 100;
      return s + ((line - discount) * num(x.gstPercent)) / 100;
    }, 0);

    const grandTotal = subTotal - discountAmount + taxAmount;

    const enteredPaid =
      form.paidAmount === "" ? grandTotal : num(form.paidAmount);

    return {
      subTotal,
      discountAmount,
      taxAmount,
      grandTotal,
      paidAmount: enteredPaid,
      balanceAmount: grandTotal - enteredPaid,
    };
  }, [cart, form.paidAmount]);

  useEffect(() => {
    initPage();
  }, []);

  const focusBarcode = () => {
    setTimeout(() => barcodeRef.current?.focus(), 150);
  };

  const initPage = async () => {
    setLoading(true);
    setPageError("");

    await Promise.allSettled([
      loadMasters(false),
      loadNextNo(),
      loadBills(false),
    ]);

    setLoading(false);
    focusBarcode();
  };

  const loadMasters = async (showError = true) => {
    const cid = companyId();

    try {
      const [c, w, i] = await Promise.all([
        client.get("/sales/customers", {
          params: cid ? { companyId: cid } : {},
        }),
        client.get("/inventory/warehouses", {
          params: cid ? { companyId: cid } : {},
        }),
        client.get("/inventory/items", {
          params: cid ? { companyId: cid } : {},
        }),
      ]);

      setCustomers(toArray(c.data));
      setWarehouses(toArray(w.data));
      setItems(toArray(i.data));
    } catch (e) {
      console.error("POS masters loading failed:", e);
      const msg = errText(e, "Failed to load customers/warehouses/items");

      if (showError) alert(msg);
      else setPageError((p) => p || msg);
    }
  };

  const loadNextNo = async () => {
    try {
      const cid = companyId();

      const res = await client.get("/pos-billing/next-no", {
        params: cid ? { companyId: cid } : {},
      });

      const nextNo =
        safe(
          val(
            res.data,
            "POSNo",
            "posNo",
            "PosNo",
            "NextNo",
            "nextNo",
            "VoucherNo",
            "voucherNo",
          ),
        ) || `POS-${Date.now().toString().slice(-5)}`;

      setForm((f) => ({
        ...f,
        posNo: nextNo,
      }));
    } catch (e) {
      console.error("Next POS no loading failed:", e);

      setForm((f) => ({
        ...f,
        posNo: f.posNo || `POS-${Date.now().toString().slice(-5)}`,
      }));
    }
  };

  const loadBills = async (showError = true) => {
    try {
      const cid = companyId();

      const res = await client.get("/pos-billing", {
        params: cid ? { companyId: cid } : {},
      });

      setRows(toArray(res.data));
    } catch (e) {
      console.error("POS bills loading failed:", e);
      const msg = errText(e, "Failed to load POS bills");

      if (showError) alert(msg);
      else setPageError((p) => p || msg);

      setRows([]);
    }
  };

  const makeCartLine = (item, qty = 1) => {
    const rate = num(val(item, "SalePrice", "salePrice", "Rate", "rate"));
    const gstPercent =
      num(val(item, "GSTPercent", "gstPercent", "GstPercent")) || 18;

    const line = num(qty) * rate;
    const tax = (line * gstPercent) / 100;

    return {
      itemId: Number(val(item, "Id", "id", "ItemId", "itemId")),
      itemCode: safe(val(item, "ItemCode", "itemCode")),
      itemName: safe(val(item, "ItemName", "itemName", "Name", "name")),
      qty: num(qty),
      rate,
      discountPercent: 0,
      gstPercent,
      taxAmount: tax,
      lineTotal: line + tax,
    };
  };

  const addByBarcode = () => {
    const code = barcode.trim().toLowerCase();
    if (!code) return;

    const item = items.find((x) => {
      const itemCode = safe(val(x, "ItemCode", "itemCode")).toLowerCase();
      const bar = safe(val(x, "Barcode", "barcode", "BarCode")).toLowerCase();
      const sku = safe(val(x, "SKU", "sku", "Sku")).toLowerCase();

      return itemCode === code || bar === code || sku === code;
    });

    if (!item) {
      alert("Item not found by barcode / SKU / item code");
      setBarcode("");
      focusBarcode();
      return;
    }

    const itemId = Number(val(item, "Id", "id", "ItemId", "itemId"));

    setCart((old) => {
      const existing = old.find((x) => x.itemId === itemId);

      if (existing) {
        return old.map((x) =>
          x.itemId === itemId
            ? recalcCartLine({ ...x, qty: num(x.qty) + 1 })
            : x,
        );
      }

      return [...old, makeCartLine(item, 1)];
    });

    setBarcode("");
    focusBarcode();
  };

  const recalcCartLine = (lineObj) => {
    const q = Math.max(num(lineObj.qty), 0);
    const line = q * num(lineObj.rate);
    const discount = (line * num(lineObj.discountPercent)) / 100;
    const tax = ((line - discount) * num(lineObj.gstPercent)) / 100;

    return {
      ...lineObj,
      qty: q,
      taxAmount: tax,
      lineTotal: line - discount + tax,
    };
  };

  const updateQty = (itemId, qty) => {
    setCart((old) =>
      old.map((x) => (x.itemId === itemId ? recalcCartLine({ ...x, qty }) : x)),
    );
  };

  const removeItem = (itemId) => {
    setCart((old) => old.filter((x) => x.itemId !== itemId));
    focusBarcode();
  };

  const clearBill = async () => {
    setCart([]);
    setBarcode("");
    setForm({
      posNo: "",
      customerId: "",
      warehouseId: "",
      paymentMode: "CASH",
      paidAmount: "",
      notes: "",
    });

    await loadNextNo();
    focusBarcode();
  };

  const save = async () => {
    if (!form.posNo.trim()) return alert("POS No is required");
    if (!form.warehouseId) return alert("Warehouse is required");
    if (cart.length === 0) return alert("Cart is empty");

    const cleanCart = cart.filter((x) => num(x.qty) > 0);
    if (cleanCart.length === 0) return alert("Qty must be greater than zero");

    try {
      setSaving(true);

      await client.post("/pos-billing", {
        companyId: companyId(),
        posNo: form.posNo.trim(),
        posDate: new Date().toISOString(),
        customerId: form.customerId ? Number(form.customerId) : null,
        warehouseId: Number(form.warehouseId),
        paymentMode: form.paymentMode,
        notes: form.notes,
        subTotal: totals.subTotal,
        discountAmount: totals.discountAmount,
        taxAmount: totals.taxAmount,
        grandTotal: totals.grandTotal,
        paidAmount: totals.paidAmount,
        balanceAmount: totals.balanceAmount,
        details: cleanCart.map((x) => ({
          itemId: x.itemId,
          qty: num(x.qty),
          rate: num(x.rate),
          discountPercent: num(x.discountPercent),
          gstPercent: num(x.gstPercent),
          taxAmount: num(x.taxAmount),
          lineTotal: num(x.lineTotal),
        })),
      });

      alert("POS bill saved successfully. Stock has been reduced.");

      await clearBill();
      await loadBills(false);
    } catch (e) {
      console.error("POS bill save failed:", e);
      alert(errText(e, "Failed to save POS bill"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-upc-scan me-2"></i>
            POS Billing
          </h6>
          <div style={styles.sub}>
            Fast retail billing with barcode, warehouse stock issue and payment
            mode
          </div>
        </div>

        <div style={styles.body}>
          {loading && (
            <div
              className="alert alert-info py-2 mb-2"
              style={{ fontSize: 11 }}
            >
              Loading POS billing data...
            </div>
          )}

          {!loading && pageError && (
            <div
              className="alert alert-warning d-flex justify-content-between align-items-center py-2 mb-2"
              style={{ fontSize: 11 }}
            >
              <span>
                <b>Warning:</b> {pageError}
              </span>
              <button
                className="btn btn-sm btn-outline-dark"
                style={{ fontSize: 10 }}
                onClick={initPage}
              >
                Retry
              </button>
            </div>
          )}

          <div className="row g-2 mb-2">
            <Stat title="Items" value={cart.length} />
            <Stat title="Sub Total" value={`₹ ${money(totals.subTotal)}`} />
            <Stat title="GST" value={`₹ ${money(totals.taxAmount)}`} />
            <Stat title="Payable" value={`₹ ${money(totals.grandTotal)}`} />
          </div>

          <div className="row g-2 mb-2">
            <Field label="POS No">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.posNo}
                onChange={(e) => setForm({ ...form, posNo: e.target.value })}
              />
            </Field>

            <Field label="Customer" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.customerId}
                onChange={(e) =>
                  setForm({ ...form, customerId: e.target.value })
                }
              >
                <option value="">Walk-in Customer</option>
                {customers.map((c, index) => {
                  const id = val(c, "Id", "id", "CustomerId", "customerId");

                  return (
                    <option key={id || index} value={id}>
                      {safe(
                        val(
                          c,
                          "CustomerName",
                          "customerName",
                          "Name",
                          "name",
                          "AccountName",
                          "accountName",
                        ),
                      )}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="Warehouse" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.warehouseId}
                onChange={(e) =>
                  setForm({ ...form, warehouseId: e.target.value })
                }
              >
                <option value="">Select Warehouse</option>
                {warehouses.map((w, index) => {
                  const id = val(w, "Id", "id", "WarehouseId", "warehouseId");

                  return (
                    <option key={id || index} value={id}>
                      {safe(val(w, "WarehouseCode", "warehouseCode"))} -{" "}
                      {safe(
                        val(
                          w,
                          "WarehouseName",
                          "warehouseName",
                          "Name",
                          "name",
                        ),
                      )}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="Payment Mode">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.paymentMode}
                onChange={(e) =>
                  setForm({ ...form, paymentMode: e.target.value })
                }
              >
                <option value="CASH">CASH</option>
                <option value="BANK">BANK</option>
                <option value="UPI">UPI</option>
                <option value="CARD">CARD</option>
              </select>
            </Field>

            <Field label="Paid Amount">
              <input
                type="number"
                className="form-control form-control-sm"
                style={styles.input}
                placeholder={money(totals.grandTotal)}
                value={form.paidAmount}
                onChange={(e) =>
                  setForm({ ...form, paidAmount: e.target.value })
                }
              />
            </Field>
          </div>

          <div
            className="row g-2 mb-2 p-2"
            style={{
              background: "#f8fafc",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <div className="col-md-8">
              <label style={styles.label}>Scan Barcode / SKU / Item Code</label>
              <input
                ref={barcodeRef}
                className="form-control form-control-sm"
                style={{
                  ...styles.input,
                  height: 40,
                  fontSize: 15,
                  fontWeight: 800,
                }}
                placeholder="Scan or type barcode and press Enter"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") addByBarcode();
                }}
                disabled={loading}
              />
            </div>

            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                style={{ ...styles.btn, height: 40 }}
                onClick={addByBarcode}
                disabled={loading}
              >
                Add Item
              </button>
            </div>

            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-outline-danger btn-sm w-100"
                style={{ ...styles.btn, height: 40 }}
                onClick={clearBill}
                disabled={saving}
              >
                Clear
              </button>
            </div>
          </div>

          <div style={styles.tableWrap} className="mb-2">
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Code",
                    "Item",
                    "Qty",
                    "Rate",
                    "Disc %",
                    "GST %",
                    "Tax",
                    "Total",
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        [
                          "Qty",
                          "Rate",
                          "Disc %",
                          "GST %",
                          "Tax",
                          "Total",
                        ].includes(h)
                          ? "text-end"
                          : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {cart.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      No items scanned
                    </td>
                  </tr>
                ) : (
                  cart.map((d) => (
                    <tr key={d.itemId}>
                      <td style={styles.td}>{d.itemCode}</td>
                      <td style={styles.td}>{d.itemName}</td>
                      <td className="text-end" style={styles.td}>
                        <input
                          type="number"
                          className="form-control form-control-sm text-end"
                          style={styles.input}
                          value={d.qty}
                          onChange={(e) => updateQty(d.itemId, e.target.value)}
                        />
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(d.rate)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(d.discountPercent)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(d.gstPercent)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(d.taxAmount)}
                      </td>
                      <td className="text-end fw-bold" style={styles.td}>
                        {money(d.lineTotal)}
                      </td>
                      <td style={styles.td}>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          style={{ fontSize: 10 }}
                          onClick={() => removeItem(d.itemId)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="row g-2 mb-2">
            <Field label="Notes" col="col-md-5">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>

            <div className="col-md-7 d-flex justify-content-end gap-2 align-items-end">
              <button
                className="btn btn-outline-secondary btn-sm"
                style={{ ...styles.btn, minWidth: 120 }}
                onClick={() => window.print()}
              >
                Print
              </button>

              <button
                className="btn btn-success btn-sm"
                style={{ ...styles.btn, minWidth: 160 }}
                onClick={save}
                disabled={saving || loading}
              >
                {saving ? "Saving..." : "Save Bill"}
              </button>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "POS No",
                    "Date",
                    "Customer",
                    "Warehouse",
                    "Mode",
                    "Total",
                    "Paid",
                    "Balance",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        ["Total", "Paid", "Balance"].includes(h)
                          ? "text-end"
                          : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      {loading ? "Loading..." : "No POS bills found"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, index) => (
                    <tr
                      key={
                        val(
                          r,
                          "Id",
                          "id",
                          "POSId",
                          "posId",
                          "PosId",
                          "BillId",
                          "billId",
                        ) || index
                      }
                    >
                      <td style={{ ...styles.td, fontWeight: 800 }}>
                        {safe(val(r, "POSNo", "posNo", "PosNo"))}
                      </td>
                      <td style={styles.td}>
                        {String(
                          val(r, "POSDate", "posDate", "PosDate") || "",
                        ).slice(0, 10)}
                      </td>
                      <td style={styles.td}>
                        {safe(
                          val(
                            r,
                            "CustomerName",
                            "customerName",
                            "Name",
                            "name",
                          ),
                        ) || "Walk-in"}
                      </td>
                      <td style={styles.td}>
                        {safe(
                          val(
                            r,
                            "WarehouseName",
                            "warehouseName",
                            "Warehouse",
                            "warehouse",
                          ),
                        )}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "PaymentMode", "paymentMode"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "GrandTotal", "grandTotal"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "PaidAmount", "paidAmount"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "BalanceAmount", "balanceAmount"))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, col = "col-md-2" }) {
  return (
    <div className={col}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-md-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
          {title}
        </div>
        <div style={{ fontSize: 15, color: "#0f766e", fontWeight: 900 }}>
          {value}
        </div>
      </div>
    </div>
  );
}
